/**
 * Amazon Cognito (USER_PASSWORD_AUTH / SRP) via `amazon-cognito-identity-js`.
 *
 * REST API Gateway's Cognito authorizer expects the **ID token** in:
 *   Authorization: Bearer <IdToken>
 *
 * Build-time env: VITE_COGNITO_USER_POOL_ID, VITE_COGNITO_CLIENT_ID,
 * VITE_COGNITO_REGION is optional (informational / future use; pool id encodes region).
 */

import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';

function readPoolConfig(): { userPoolId: string; clientId: string } {
  const env = import.meta.env;
  const userPoolId = (env.VITE_COGNITO_USER_POOL_ID as string | undefined)?.trim() ?? '';
  const clientId = (env.VITE_COGNITO_CLIENT_ID as string | undefined)?.trim() ?? '';
  if (!userPoolId || !clientId) {
    const missing: string[] = [];
    if (!userPoolId) missing.push('VITE_COGNITO_USER_POOL_ID');
    if (!clientId) missing.push('VITE_COGNITO_CLIENT_ID');
    throw new Error(
      `[cognito-client] Missing: ${missing.join(', ')}. ` +
        'Copy `frontend/.env.example` to `frontend/.env.local` and set Cognito outputs from CDK.',
    );
  }
  return { userPoolId, clientId };
}

let pool: CognitoUserPool | null = null;

export function getUserPool(): CognitoUserPool {
  if (pool) return pool;
  const cfg = readPoolConfig();
  pool = new CognitoUserPool({
    UserPoolId: cfg.userPoolId,
    ClientId: cfg.clientId,
  });
  return pool;
}

export class NewPasswordRequiredError extends Error {
  readonly code = 'NEW_PASSWORD_REQUIRED' as const;

  constructor(public readonly cognitoUser: CognitoUser) {
    super('NEW_PASSWORD_REQUIRED');
    this.name = 'NewPasswordRequiredError';
    Object.setPrototypeOf(this, NewPasswordRequiredError.prototype);
  }
}

export async function completeNewPassword(user: CognitoUser, newPassword: string): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(
      newPassword,
      {},
      {
        onSuccess: resolve,
        onFailure: reject,
      },
    );
  });
}

export async function signInWithPassword(email: string, password: string): Promise<CognitoUserSession> {
  const userPool = getUserPool();
  const user = new CognitoUser({
    Username: email.trim().toLowerCase(),
    Pool: userPool,
  });
  const details = new AuthenticationDetails({
    Username: email.trim().toLowerCase(),
    Password: password,
  });
  return new Promise((resolve, reject) => {
    user.authenticateUser(details, {
      onSuccess: resolve,
      onFailure: reject,
      newPasswordRequired: () => {
        reject(new NewPasswordRequiredError(user));
      },
    });
  });
}

export function getCurrentCognitoUser(): CognitoUser | null {
  return getUserPool().getCurrentUser();
}

/**
 * Returns the Cognito **ID** JWT for API calls (API Gateway Cognito authorizer).
 */
export async function getIdToken(forceRefresh = false): Promise<string | null> {
  const user = getCurrentCognitoUser();
  if (!user) return null;
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) {
        resolve(null);
        return;
      }
      if (forceRefresh) {
        user.refreshSession(session.getRefreshToken(), (e, refreshed) => {
          if (e || !refreshed) {
            resolve(null);
            return;
          }
          resolve(refreshed.getIdToken().getJwtToken());
        });
      } else {
        resolve(session.getIdToken().getJwtToken());
      }
    });
  });
}

export async function signOutCognito(): Promise<void> {
  const user = getCurrentCognitoUser();
  if (user) {
    user.signOut();
  }
}
